import { useInfiniteQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { startOfWeek, subWeeks } from 'date-fns'
import { Fragment, type JSX, useMemo } from 'react'
import { unique } from 'remeda'
import { z } from 'zod'
import Callout from '~/components/callout'
import { DateRangePicker } from '~/components/date-picker'
import {
  ChannelsCommandList,
  CommandPopover,
  GroupsCommandList,
  ScorecardsCommandList,
  UsersCommandList,
  WorkspacesCommandList,
} from '~/features/command-lists'
import { useDatastore } from '~/features/datastore'
import { useFilterAccess } from '~/features/filter-control'
import { useTimespanPreference, useWeekStartsOn } from '~/hooks'
import { useTRPC } from '~/services/trpc'

import { FilterCollection } from '@hap/analytics'
import { Button, Heading1, Switch, Table } from '@hap/components'
import { Timespan } from '@hap/utils'

import {
  hasEditableMetricFilters,
  preserveReadOnlyMetricFilters,
} from './-components/metric-filter-access'
import { MetricFilters, metricFilters } from './-components/metric-widget'
import { ScorecardSection, criteriaScope } from './-components/scorecard-section'
import { ScorecardTrends } from './-components/scorecard-trends'

export const searchParamsSchema = z.object({
  filters: metricFilters.default({}),
  scope: criteriaScope.default('All'),
  timespan: Timespan.Info.optional(),
})

export type SearchParams = z.infer<typeof searchParamsSchema>

export const Route = createFileRoute('/_authenticated/$alias/_app/indicators/')({
  component: RouteComponent,
  validateSearch: searchParamsSchema,
})

function RouteComponent(): JSX.Element {
  const { isFilterAllowed } = useFilterAccess()
  const trpc = useTRPC()
  const { filters, scope: goalFilter, timespan: selectedTimespan } = Route.useSearch()
  const weekStartsOn = useWeekStartsOn()
  const { timespan, setTimespanPreference } = useTimespanPreference({
    selectedTimespan,
    startOffset: (date) => startOfWeek(subWeeks(date, 3), { weekStartsOn }),
  })
  const navigate = useNavigate({ from: Route.fullPath })

  const scorecards = useDatastore(FilterCollection.Scorecards)
  const workspaces = useDatastore(FilterCollection.Workspaces)
  const scorecardIds = useMemo<string[]>(() => {
    const scorecardIds = filters.scorecardIds?.values
      ? [...filters.scorecardIds.values]
      : []
    if (filters.workspaceIds?.values && scorecards.data) {
      filters.workspaceIds?.values.forEach((workspaceId) => {
        scorecards.data?.forEach((scorecard) => {
          if (workspaceId.includes(scorecard.meta.workspaceId)) {
            scorecardIds.push(scorecard.id)
          }
        })
      })
    }
    if (filters.channelIds?.values && scorecards.data) {
      filters.channelIds.values.forEach((channelId) => {
        const matchingWorkspaces = workspaces.data.filter((w) =>
          w.meta.channelIds?.includes(channelId),
        )
        matchingWorkspaces.forEach((workspace) => {
          scorecards.data?.forEach((scorecard) => {
            if (workspace.id.includes(scorecard.meta.workspaceId)) {
              scorecardIds.push(scorecard.id)
            }
          })
        })
      })
    }
    return unique(scorecardIds)
  }, [filters.channelIds, filters.workspaceIds, filters.scorecardIds])

  const { data, isFetchingNextPage, isFetching } = useInfiniteQuery(
    trpc.scorecardSections.getAll.infiniteQueryOptions(
      {
        limit: 10,
        scorecardIds: scorecardIds?.length > 0 ? scorecardIds : null,
      },
      {
        getNextPageParam: (lastPage) => {
          return lastPage.nextCursor
        },
      },
    ),
  )

  const daterange = useMemo(
    () => Timespan.toStaticTimespan(timespan, weekStartsOn),
    [timespan, weekStartsOn],
  )

  return (
    <div className="gutters my-10 shrink grow space-y-10">
      <header className="space-y-3">
        <Heading1>Scorecard Indicators</Heading1>
        <div className="flex flex-wrap items-center gap-2">
          {hasEditableMetricFilters(filters, isFilterAllowed) && (
            <Button
              variant="ghost"
              onClick={() =>
                navigate({
                  search: (prev: SearchParams) => ({
                    ...prev,
                    filters: preserveReadOnlyMetricFilters(prev.filters, isFilterAllowed),
                  }),
                })
              }
              icon="arrow-counter-clockwise"
            >
              Reset
            </Button>
          )}

          <DateRangePicker
            value={timespan}
            onValueChange={(value) => {
              setTimespanPreference(value)
              navigate({
                search: (prev: SearchParams) => ({ ...prev, timespan: value }),
              })
            }}
          />

          <CommandPopover
            filterField="workspaceId"
            label="Workspaces"
            collection={FilterCollection.Workspaces}
            selected={filters.workspaceIds?.values || []}
          >
            <WorkspacesCommandList
              multiselect
              onSelect={(ids) =>
                handleFilterValueChange({
                  field: 'workspaceIds',
                  values: ids,
                })
              }
            />
          </CommandPopover>

          <CommandPopover
            filterField="channelId"
            collection={FilterCollection.Channels}
            label="Channels"
            selected={filters.channelIds?.values || []}
          >
            <ChannelsCommandList
              multiselect
              onSelect={(ids) =>
                handleFilterValueChange({
                  field: 'channelIds',
                  values: ids,
                })
              }
            />
          </CommandPopover>

          <CommandPopover
            filterField="scorecardId"
            collection={FilterCollection.Scorecards}
            label="Scorecards"
            selected={filters.scorecardIds?.values || []}
          >
            <ScorecardsCommandList
              multiselect
              onSelect={(ids) =>
                handleFilterValueChange({
                  field: 'scorecardIds',
                  values: ids,
                })
              }
              contextFilters={{
                workspaceId: {
                  $eq: filters.workspaceIds?.values || [],
                },
              }}
            />
          </CommandPopover>

          <CommandPopover
            filterField="groupIds"
            collection={FilterCollection.Groups}
            label="Groups"
            selected={filters.groupIds?.values || []}
          >
            <GroupsCommandList
              multiselect
              onSelect={(ids) =>
                handleFilterValueChange({
                  field: 'groupIds',
                  values: ids,
                })
              }
            />
          </CommandPopover>
          <CommandPopover
            filterField="assignedSupervisorId"
            collection={FilterCollection.Users}
            label="Reports To"
            selected={filters.reportToIds?.values || []}
          >
            <UsersCommandList
              multiselect
              onSelect={(ids) =>
                handleFilterValueChange({
                  field: 'reportToIds',
                  values: ids,
                })
              }
              contextFilters={{
                subordinates: {
                  $exists: true,
                },
              }}
            />
          </CommandPopover>
          <CommandPopover
            filterField="assignedTo"
            collection={FilterCollection.Users}
            label="Members"
            selected={filters.assignedTo?.values || []}
          >
            <UsersCommandList
              multiselect
              onSelect={(ids) =>
                handleFilterValueChange({
                  field: 'assignedTo',
                  values: ids,
                })
              }
            />
          </CommandPopover>
          {(isFilterAllowed('reviewedAt') || !!filters.reviewed) && (
            <label className="flex items-center gap-2">
              <Switch
                checked={!!filters.reviewed}
                disabled={!isFilterAllowed('reviewedAt')}
                onCheckedChange={(checked: boolean) =>
                  handleFilterValueChange({ field: 'reviewed', values: checked })
                }
              />
              <div>Show Reviewed</div>
            </label>
          )}
        </div>

        <div>
          <ScorecardTrends filters={filters} daterange={daterange} />
        </div>

        <div className="flex flex-wrap justify-between gap-4">
          <div className="space-x-1">
            <Button
              variant={goalFilter === 'All' ? 'secondary' : 'ghost'}
              onClick={() =>
                navigate({
                  search: (prev: SearchParams) => ({ ...prev, scope: 'All' }),
                })
              }
            >
              All
            </Button>
            <Button
              variant={goalFilter === 'Below' ? 'secondary' : 'ghost'}
              onClick={() =>
                navigate({
                  search: (prev: SearchParams) => ({ ...prev, scope: 'Below' }),
                })
              }
            >
              Areas of opportunity
            </Button>
            <Button
              variant={goalFilter === 'Above' ? 'secondary' : 'ghost'}
              onClick={() =>
                navigate({
                  search: (prev: SearchParams) => ({ ...prev, scope: 'Above' }),
                })
              }
            >
              Areas of strength
            </Button>
          </div>
        </div>
      </header>

      {data?.pages[0]?.data?.length === 0 && !isFetching && (
        <Callout
          title="Get started building your scorecard"
          description="To get started building your scorecard, create a new workspace to hold your scorecards."
          actions={[
            {
              label: 'Create a new workspace',
              to: '/settings/workspaces?new=true',
              variant: 'default',
            },
          ]}
        />
      )}
      <Table className="-m-4">
        {data?.pages.map((page, i) => {
          return (
            <Fragment key={i}>
              {page.data.map((section, index) => {
                return (
                  <ScorecardSection
                    key={index}
                    section={section}
                    filters={filters}
                    goalFilter={goalFilter}
                    daterange={daterange}
                    timespan={timespan}
                  />
                )
              })}
            </Fragment>
          )
        })}
      </Table>
      <div>{isFetching && !isFetchingNextPage ? 'Fetching...' : null}</div>
    </div>
  )

  function handleFilterValueChange(opts: {
    field: keyof MetricFilters
    values: string[] | boolean
  }) {
    navigate({
      search: (prev) => {
        function compileFilters() {
          if (typeof opts.values === 'boolean') {
            if (opts.values === true) {
              return {
                ...prev.filters,
                [opts.field]: { op: 'isNotNull' },
              }
            } else {
              return Object.fromEntries(
                Object.entries(prev.filters).filter(([key]) => key !== opts.field),
              )
            }
          }

          if (prev.filters[opts.field]?.op === 'isNotNull') {
            return {
              ...prev.filters,
            }
          }

          return {
            ...prev.filters,
            [opts.field]:
              opts.values.length > 0 ? { op: 'in', values: opts.values } : undefined,
          }
        }

        return {
          ...prev,
          filters: compileFilters(),
        }
      },
    })
  }
}
